import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiGrid, FiBarChart2, FiCompass, FiPlus, FiClock, FiUsers, FiMessageSquare } from 'react-icons/fi';
import { PiArticleLight } from 'react-icons/pi';
import { APP_NAME, getCurrentYear } from '../../../../../config/branding';
import {
  applyProfileAvatarImgFallback,
  buildProfilePictureUrlForMode,
} from '../../../../MainHeader/profilePictureUrl';
import Tooltip from '../../../../../layout/ReusableComponent/Tooltip/Tooltip';
import { ShellSideNavItem } from './shellSideNav';

function ShellSideNavHomeSkeleton({ isNavCollapsed }) {
  return (
    <div
      className="support-open-disc-shell-side-item support-open-disc-shell-side-item--profile-home-skel"
      aria-hidden
    >
      <span className="support-open-disc-shell-side-item-avatar-wrap" aria-hidden>
        <span className="support-open-disc-shell-side-item-skel-avatar" />
      </span>
      {!isNavCollapsed ? <span className="support-open-disc-shell-side-item-skel-label" /> : null}
    </div>
  );
}

function ShellCreatePostButton({ isNavCollapsed, isActive, onClick }) {
  const button = (
    <button
      type="button"
      className={`support-open-disc-shell-sidebar-create-post-btn${isActive ? ' is-active' : ''}`}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
    >
      <FiPlus aria-hidden color="var(--brand-50, #f5ebe0)"/>
      <span className="support-open-disc-shell-sidebar-create-post-btn-label">Post</span>
    </button>
  );

  if (!isNavCollapsed) return button;

  return (
    <Tooltip text="Create post" position="right" className="tooltip-container--block">
      {button}
    </Tooltip>
  );
}

/**
 * Posts shell: primary nav card + View later / My posts card (posts open in main area).
 */
export default function PostsShellSidebar({
  profile,
  profileResolved = true,
  drawerOpenClass = '',
  ariaHidden,
  isNavCollapsed = false,
  activeHomeSort = 'latest',
  isViewLaterActive = false,
  isMyPostsActive = false,
  isClubsActive = false,
  isMessagesActive = false,
  isCreatePostActive = false,
  hasUnreadMessages = false,
  messagesUnreadCount = 0,
  /** When true, no primary nav item (Home / Popular / etc.) is highlighted */
  suppressNavActive = false,
  onHome,
  onPopular,
  onRecommendations,
  onClubs,
  onMessages,
  onCreatePost,
  onViewLaterSection,
  onMyPostsSection,
  viewLaterItems = [],
}) {
  const showPersonalNav = !!profile?.userId;
  const personalNavActive = isViewLaterActive || isMyPostsActive;
  const homeNavActive =
    !suppressNavActive && !personalNavActive && !isClubsActive && !isMessagesActive;
  const profileHomeSortActive = activeHomeSort === 'recommended';
  const recommendationsSortActive = activeHomeSort === 'latest' && showPersonalNav;
  const viewLaterCount = viewLaterItems.length;
  const viewLaterTooltip =
    viewLaterCount > 0 ? `View later (${viewLaterCount} saved)` : 'View later';
  const homeNavLabel = useMemo(() => {
    const name = String(profile?.name || '').trim();
    if (name) return name;
    const email = String(profile?.email || '').trim();
    if (email) return email.split('@')[0];
    return 'Home';
  }, [profile?.name, profile?.email]);
  const homeNavAvatarSrc = useMemo(
    () =>
      buildProfilePictureUrlForMode(profile?.profilePictureUrl, '', profile?.profileMode, {
        userId: profile?.userId,
        name: profile?.displayName || profile?.name,
      }),
    [profile?.profilePictureUrl, profile?.profileMode, profile?.userId, profile?.displayName, profile?.name]
  );
  const showProfileHomeNav = profileResolved && !!profile?.userId;

  return (
    <div
      className={`support-open-disc-shell-sidebar-col${drawerOpenClass}`}
      aria-label="Posts navigation"
      aria-hidden={ariaHidden}
    >
      <div className="support-open-disc-shell-sidebar-col-inner">
      <aside className="support-open-disc-shell-sidebar support-open-disc-shell-sidebar--primary">
        <nav className="support-open-disc-shell-side-nav" aria-label="Posts">
          {!profileResolved ? (
            <ShellSideNavHomeSkeleton isNavCollapsed={isNavCollapsed} />
          ) : (
            <ShellSideNavItem
              label={homeNavLabel}
              tooltipText={showProfileHomeNav ? homeNavLabel : 'Home'}
              isNavCollapsed={isNavCollapsed}
              iconTone={showProfileHomeNav ? undefined : 'home'}
              className={`${homeNavActive && profileHomeSortActive ? ' is-active' : ''}${
                showProfileHomeNav ? ' support-open-disc-shell-side-item--profile-home' : ''
              }`}
              onClick={onHome}
              icon={
                showProfileHomeNav ? (
                  <span className="support-open-disc-shell-side-item-avatar-wrap">
                    <img
                      src={homeNavAvatarSrc}
                      alt=""
                      className="support-open-disc-shell-side-item-avatar"
                      onError={(e) => {
                        applyProfileAvatarImgFallback(e.currentTarget, {
                          userId: profile?.userId,
                          name: profile?.displayName || profile?.name,
                        });
                      }}
                    />
                  </span>
                ) : (
                  <FiGrid aria-hidden strokeWidth={2.75} />
                )
              }
            />
          )}
          <ShellSideNavItem
            label="Popular"
            isNavCollapsed={isNavCollapsed}
            iconTone="popular"
            className={homeNavActive && activeHomeSort === 'votes' ? ' is-active' : ''}
            onClick={onPopular}
            icon={<FiBarChart2 aria-hidden strokeWidth={2.2} />}
          />
          <ShellSideNavItem
            label="Recommendations"
            isNavCollapsed={isNavCollapsed}
            iconTone="recommendations"
            className={homeNavActive && recommendationsSortActive ? ' is-active' : ''}
            onClick={onRecommendations}
            icon={<FiCompass aria-hidden strokeWidth={2.2} />}
          />
          <ShellSideNavItem
            label="Clubs"
            isNavCollapsed={isNavCollapsed}
            iconTone="clubs"
            className={!personalNavActive && isClubsActive ? ' is-active' : ''}
            onClick={onClubs}
            icon={<FiUsers aria-hidden strokeWidth={2.2} />}
          />
          {showPersonalNav ? (
            <ShellSideNavItem
              label="Messages"
              tooltipText={hasUnreadMessages ? 'Messages (unread)' : 'Messages'}
              isNavCollapsed={isNavCollapsed}
              iconTone="messages"
              className={!personalNavActive && isMessagesActive ? ' is-active' : ''}
              onClick={onMessages}
              icon={<FiMessageSquare aria-hidden strokeWidth={2.2} />}
              trailing={
                messagesUnreadCount > 0 ? (
                  <span
                    className="support-open-disc-shell-side-badge"
                    aria-label={`${messagesUnreadCount} unread messages`}
                  >
                    {messagesUnreadCount > 99 ? '99+' : messagesUnreadCount}
                  </span>
                ) : null
              }
            />
          ) : null}
        </nav>
      </aside>

      {showPersonalNav ? (
        <>
          <div
            className="support-open-disc-shell-sidebar-separator"
            role="separator"
            aria-hidden
          />
          <aside
            className={`support-open-disc-shell-sidebar support-open-disc-shell-sidebar--view-later${
              personalNavActive ? ' is-section-active' : ''
            }`}
            aria-label="Your posts"
          >
            <ShellSideNavItem
              label="View later"
              tooltipText={viewLaterTooltip}
              isNavCollapsed={isNavCollapsed}
              iconTone="view-later"
              className={` support-open-disc-view-later-side-nav${isViewLaterActive ? ' is-active' : ''}`}
              onClick={onViewLaterSection}
              icon={<FiClock aria-hidden strokeWidth={2.75} />}
              trailing={
                viewLaterCount ? (
                  <span className="support-open-disc-shell-side-badge" aria-label={`${viewLaterCount} saved`}>
                    {viewLaterCount}
                  </span>
                ) : null
              }
            />
            <ShellSideNavItem
              label="My posts"
              isNavCollapsed={isNavCollapsed}
              iconTone="my-posts"
              className={` support-open-disc-view-later-side-nav${isMyPostsActive ? ' is-active' : ''}`}
              onClick={onMyPostsSection}
              icon={<PiArticleLight aria-hidden strokeWidth={2.75} />}
            />
          </aside>
        </>
      ) : null}

      <div className="support-open-disc-shell-sidebar-create-post-wrap">
        <ShellCreatePostButton
          isNavCollapsed={isNavCollapsed}
          isActive={isCreatePostActive}
          onClick={onCreatePost}
        />
      </div>
      </div>
      <footer className="support-open-disc-shell-sidebar-col-footer" aria-label="Site footer">
        <p className="support-open-disc-shell-sidebar-col-copyright">
          © {getCurrentYear()} {APP_NAME} All rights reserved
        </p>
        <Link to="/docs" className="support-open-disc-shell-sidebar-col-doc-link">
          Documentation
        </Link>
      </footer>
    </div>
  );
}
